import { useRef, useState } from 'react';

import { ApiError } from '@mds/shared/lib/api';
import type { FileVisibility } from '@mds/shared/types/file';

import Button from '@/components/ui/button';
import Select from '@mds/shared/ui/select';
import {
  ACCEPTED_TYPES,
  checkFile,
  formatBytes,
  uploadFile,
  type UploadPhase,
} from '@/files/upload';

interface Job {
  id: number;
  name: string;
  size: number;
  phase: UploadPhase | 'done' | 'failed';
  fraction: number;
  error?: string;
}

const PHASE_LABEL: Record<Job['phase'], string> = {
  converting: 'Converting',
  hashing: 'Checksumming',
  uploading: 'Uploading',
  verifying: 'Verifying',
  previewing: 'Making preview',
  sizing: 'Making display sizes',
  done: 'Ready',
  failed: 'Failed',
};

let nextId = 0;

/**
 * What the API refuses beyond type and size, which `checkFile` covers: a file
 * with no bytes, and a name longer than it stores.
 */
const checkUpload = (file: File): string | null => {
  if (file.size === 0) return 'The file is empty. Choose one with something in it.';
  if (file.name.trim().length > 255) {
    return 'The filename is over 255 characters. Rename the file, then add it again.';
  }
  return checkFile(file);
};

/**
 * Drop or choose files, then watch each one through the three-step upload.
 *
 * Files are uploaded one at a time rather than in parallel: each holds its
 * whole body in memory to checksum it, and the upload URLs are short-lived, so
 * a queue is kinder than twenty simultaneous 20 MB requests.
 *
 * Everything goes into `folderId` (null for the top level), which `destination`
 * names on screen so it is never a surprise where a file landed.
 */
export default function FileUploader({
  folderId,
  destination,
  onUploaded,
}: {
  folderId: string | null;
  destination: string;
  onUploaded: () => void | Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [visibility, setVisibility] = useState<FileVisibility>('private');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (id: number, patch: Partial<Job>) =>
    setJobs((cur) => cur.map((j) => (j.id === id ? { ...j, ...patch } : j)));

  const run = async (chosen: File[]) => {
    if (chosen.length === 0) return;
    setBusy(true);
    for (const file of chosen) {
      const id = (nextId += 1);
      const rejection = checkUpload(file);
      setJobs((cur) => [
        {
          id,
          name: file.name,
          size: file.size,
          phase: rejection ? 'failed' : 'hashing',
          fraction: 0,
          error: rejection ?? undefined,
        },
        ...cur,
      ]);
      if (rejection) continue;

      try {
        const stored = await uploadFile(
          file,
          visibility,
          (phase, fraction) => update(id, { phase, fraction }),
          folderId,
        );
        // Report what was actually stored, not what was chosen: for a photo
        // those differ by an order of magnitude after conversion.
        update(id, {
          phase: 'done',
          fraction: 1,
          name: stored.originalFilename,
          size: stored.sizeBytes,
        });
      } catch (e) {
        update(id, {
          phase: 'failed',
          error: e instanceof ApiError ? e.message : (e as Error).message,
        });
      }
    }
    setBusy(false);
    await onUploaded();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-2">
          <span className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
            Visibility
          </span>
          <Select
            variant="field"
            value={visibility}
            onChange={(v) => setVisibility(v as FileVisibility)}
            options={[
              { value: 'private', label: 'Private (staff only)' },
              { value: 'public', label: 'Public (anyone with the link)' },
            ]}
            aria-label="Visibility for new uploads"
          />
        </label>
        <p className="m-0 max-w-[360px] text-base text-ink-muted">
          Applies to whatever you add next, and can be changed per file afterwards. The bucket
          itself stays private either way.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void run([...e.dataTransfer.files]);
        }}
        className={
          'flex flex-col items-center gap-3 rounded-2xl border border-dashed px-6 py-10 text-center transition-colors ' +
          (dragging ? 'border-brand-edge bg-brand-wash' : 'border-hairline bg-raised')
        }
      >
        <p className="m-0 text-lg font-semibold">Drop files here</p>
        <p className="m-0 text-base">
          Adding to <strong>{destination}</strong>
        </p>
        <p className="m-0 text-base text-ink-muted">
          Photos are converted to WebP automatically · PDF up to 20 MB
        </p>
        <input
          ref={input}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          className="hidden"
          onChange={(e) => {
            void run([...(e.target.files ?? [])]);
            // Clear it, or choosing the same file twice in a row does nothing.
            e.target.value = '';
          }}
        />
        <Button variant="quiet" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading…' : 'Choose files'}
        </Button>
      </div>

      {jobs.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {jobs.map((job) => (
            <li
              key={job.id}
              className={
                'rounded-xl border px-4 py-3 ' +
                (job.phase === 'failed'
                  ? 'border-danger-edge bg-danger-wash'
                  : job.phase === 'done'
                    ? 'border-brand-edge/40 bg-brand-wash'
                    : 'border-hairline bg-surface')
              }
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-base font-semibold">{job.name}</span>
                <span className="font-mono text-sm text-ink-muted">
                  {formatBytes(job.size)} · {PHASE_LABEL[job.phase]}
                </span>
              </div>

              {job.phase !== 'failed' && job.phase !== 'done' && (
                <span className="mt-2 block h-2 overflow-hidden rounded-full border border-hairline bg-ink/10">
                  <span
                    className="block h-full rounded-full bg-brand transition-[width] duration-(--duration-fast)"
                    style={{
                      // Hashing and verifying have no byte progress of their own,
                      // so they show as a part-filled bar rather than a fake count.
                      width:
                        job.phase === 'uploading'
                          ? `${Math.round(job.fraction * 100)}%`
                          : job.phase === 'hashing'
                            ? '8%'
                            : job.phase === 'previewing' || job.phase === 'sizing'
                              ? '99%'
                              : '96%',
                    }}
                  />
                </span>
              )}

              {job.error && (
                <p role="alert" className="m-0 mt-2 text-base text-danger-text">
                  {/* The name is on screen above; an alert is read on its own. */}
                  <span className="sr-only">{job.name}: </span>
                  {job.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
